import React, { useEffect, useState, useCallback } from 'react';

const emptyForm = {
  expense_date: new Date().toISOString().slice(0, 10),
  category_id: '',
  department_id: '',
  vendor_id: '',
  invoice_no: '',
  invoice_date: '',
  payment_to_type: 'VENDOR',
  payment_mode: 'CASH',
  payment_reference_no: '',
  payee_name: '',
  payee_mobile: '',
  description: '',
  subtotal: '',
  cgst_amount: '0',
  sgst_amount: '0',
  igst_amount: '0',
  tds_section_code: '',
  tds_amount: '0',
  total_amount: '',
  is_itc_eligible: true,
  is_rcm: false
};

const PAYMENT_TO_TYPES = ['VENDOR', 'STAFF_USER', 'PAYEE', 'WALK_IN'];
const PAYMENT_MODES = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE', 'CARD', 'PETTY_CASH', 'OTHER'];
const TDS_SECTIONS = ['', '94C', '94J', '94I', '94H', '94A'];

function PmsExpenseEntry({ token, selectedProperty }) {
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadMasters = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [catRes, deptRes, venRes] = await Promise.all([
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() }),
        fetch('/api/v1/expense/vendors', { headers: headers() })
      ]);
      const [catData, deptData, venData] = await Promise.all([catRes.json(), deptRes.json(), venRes.json()]);
      if (catRes.ok) setCategories(catData.rows || catData || []);
      if (deptRes.ok) setDepartments(deptData.rows || deptData || []);
      if (venRes.ok) setVendors(venData.rows || venData || []);
    } catch {
      setError('Network error loading masters');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Matches backend validateExpensePayload (§4.1.6):
  //   total_amount = subtotal + cgst + sgst + igst + cess − tds_amount
  // TDS is subtracted because the Total Amount represents the NET payable
  // to the vendor after TDS deduction (what actually leaves the bank).
  const computeTotal = () => {
    const sub = Number(form.subtotal || 0);
    const cg = Number(form.cgst_amount || 0);
    const sg = Number(form.sgst_amount || 0);
    const ig = Number(form.igst_amount || 0);
    const tds = Number(form.tds_amount || 0);
    const tot = sub + cg + sg + ig - tds;
    set('total_amount', tot.toFixed(2));
  };

  const buildPayload = () => ({
    expense_date: form.expense_date,
    category_id: Number(form.category_id) || null,
    department_id: Number(form.department_id) || null,
    vendor_id: form.vendor_id || null,
    invoice_no: form.invoice_no || null,
    invoice_date: form.invoice_date || null,
    payment_to_type: form.payment_to_type,
    payment_mode: form.payment_mode,
    payment_reference_no: form.payment_reference_no || null,
    payee_name: form.payee_name || null,
    payee_mobile: form.payee_mobile || null,
    description: form.description || null,
    subtotal: Number(form.subtotal || 0),
    cgst_amount: Number(form.cgst_amount || 0),
    sgst_amount: Number(form.sgst_amount || 0),
    igst_amount: Number(form.igst_amount || 0),
    tds_section_code: form.tds_section_code || null,
    tds_amount: Number(form.tds_amount || 0),
    total_amount: Number(form.total_amount || 0),
    is_itc_eligible: !!form.is_itc_eligible,
    is_rcm: !!form.is_rcm,
    lines: [],
    mappings: []
  });

  const validate = () => {
    if (!form.expense_date) return 'Expense date required';
    if (!form.category_id) return 'Category required';
    if (!form.department_id) return 'Department required';
    if (!form.payment_to_type) return 'Payment To Type required';
    if (!form.payment_mode) return 'Payment Mode required';
    if (!form.total_amount || Number(form.total_amount) <= 0) return 'Total amount must be > 0';
    return null;
  };

  const saveDraft = async (alsoSubmit = false) => {
    if (!selectedProperty?.id) return setError('Select property first');
    const v = validate();
    if (v) return setError(v);
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch('/api/v1/expense/', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(buildPayload())
      });
      const data = await res.json();
      if (!res.ok) {
        // Backend may return { error } OR { errors: [{code, message}], code }.
        // Pick the most specific message available.
        let msg = data.error;
        if (!msg && Array.isArray(data.errors) && data.errors.length) {
          msg = data.errors.map(e => e.message || e.msg || e.code).filter(Boolean).join('; ');
        }
        setError(msg || `Failed to save (${res.status})`);
        return;
      }
      if (alsoSubmit && data.expense_id) {
        const subRes = await fetch(`/api/v1/expense/${data.expense_id}/submit`, {
          method: 'POST',
          headers: headers({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({})
        });
        const subData = await subRes.json();
        if (!subRes.ok) {
          setError('Saved as DRAFT but submit failed: ' + (subData.error || 'Unknown'));
          return;
        }
        setSuccess(`Expense ${data.expense_no} saved and submitted for approval.`);
      } else {
        setSuccess(`Expense ${data.expense_no} saved as DRAFT.`);
      }
      setForm(emptyForm);
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.82rem', color: '#374151', minWidth: 0 };
  const inputStyle = { padding: '0.55rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', background: '#fff' };
  const sectionStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem 1.2rem', marginBottom: '0.9rem' };
  const sectionHeader = { margin: '0 0 0.7rem 0', color: '#111827', fontSize: '0.95rem', fontWeight: 600, paddingBottom: '0.5rem', borderBottom: '1px solid #f3f4f6' };
  const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.8rem' };

  // Live summary math
  const sub = Number(form.subtotal || 0);
  const taxes = Number(form.cgst_amount || 0) + Number(form.sgst_amount || 0) + Number(form.igst_amount || 0);
  const tds = Number(form.tds_amount || 0);
  const computedTotal = sub + taxes - tds;
  const totalEntered = Number(form.total_amount || 0);
  const totalMismatch = totalEntered > 0 && Math.abs(computedTotal - totalEntered) > 0.01 && sub > 0;

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="page-shell" style={{ paddingBottom: '5rem' }}>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>New Expense</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property from Hotel Config first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.7rem 1rem', borderRadius: 6, marginBottom: '0.8rem', borderLeft: '4px solid #dc2626', fontSize: '0.88rem' }}>⚠ {error}</div>}
      {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.7rem 1rem', borderRadius: 6, marginBottom: '0.8rem', borderLeft: '4px solid #16a34a', fontSize: '0.88rem' }}>✓ {success}</div>}

      {/* ─── Section 1: Expense Details ─── */}
      <div style={sectionStyle}>
        <h3 style={sectionHeader}>Expense Details</h3>
        <div style={grid4}>
          <label style={fieldLabel}>
            <span>Expense Date *</span>
            <input type="date" value={form.expense_date} onChange={e => set('expense_date', e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldLabel}>
            <span>Category *</span>
            <select value={form.category_id} onChange={e => set('category_id', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
            </select>
          </label>
          <label style={fieldLabel}>
            <span>Department *</span>
            <select value={form.department_id} onChange={e => set('department_id', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.dept_name}</option>)}
            </select>
          </label>
          <label style={fieldLabel}>
            <span>Vendor</span>
            <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)} style={inputStyle}>
              <option value="">— None —</option>
              {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* ─── Section 2: Invoice & Payment ─── */}
      <div style={sectionStyle}>
        <h3 style={sectionHeader}>Invoice & Payment</h3>
        <div style={grid4}>
          <label style={fieldLabel}>
            <span>Invoice No</span>
            <input value={form.invoice_no} onChange={e => set('invoice_no', e.target.value)} style={inputStyle} placeholder="e.g. INV/2026/0042" />
          </label>
          <label style={fieldLabel}>
            <span>Invoice Date</span>
            <input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldLabel}>
            <span>Payment To Type *</span>
            <select value={form.payment_to_type} onChange={e => set('payment_to_type', e.target.value)} style={inputStyle}>
              {PAYMENT_TO_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={fieldLabel}>
            <span>Payment Mode *</span>
            <select value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)} style={inputStyle}>
              {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label style={fieldLabel}>
            <span>Payment Reference No</span>
            <input value={form.payment_reference_no} onChange={e => set('payment_reference_no', e.target.value)} style={inputStyle} placeholder="UPI ref / cheque no / etc." />
          </label>
          <label style={fieldLabel}>
            <span>Payee Name</span>
            <input value={form.payee_name} onChange={e => set('payee_name', e.target.value)} style={inputStyle} placeholder="If different from vendor" />
          </label>
          <label style={fieldLabel}>
            <span>Payee Mobile</span>
            <input value={form.payee_mobile} onChange={e => set('payee_mobile', e.target.value)} style={inputStyle} placeholder="10-digit, optional" />
          </label>
          <div /> {/* spacer for 4-col alignment */}
          <label style={{ ...fieldLabel, gridColumn: '1 / -1' }}>
            <span>Description / Purpose</span>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Brief description of what this expense is for" />
          </label>
        </div>
      </div>

      {/* ─── Section 3: Amount Breakdown ─── */}
      <div style={sectionStyle}>
        <h3 style={sectionHeader}>Amount Breakdown</h3>
        <div style={{ ...grid4, marginBottom: '0.7rem' }}>
          <label style={fieldLabel}>
            <span>Subtotal (excl. tax)</span>
            <input type="number" step="0.01" value={form.subtotal} onChange={e => set('subtotal', e.target.value)} onBlur={computeTotal} style={inputStyle} placeholder="0.00" />
          </label>
          <label style={fieldLabel}>
            <span>CGST</span>
            <input type="number" step="0.01" value={form.cgst_amount} onChange={e => set('cgst_amount', e.target.value)} onBlur={computeTotal} style={inputStyle} placeholder="0.00" />
          </label>
          <label style={fieldLabel}>
            <span>SGST</span>
            <input type="number" step="0.01" value={form.sgst_amount} onChange={e => set('sgst_amount', e.target.value)} onBlur={computeTotal} style={inputStyle} placeholder="0.00" />
          </label>
          <label style={fieldLabel}>
            <span>IGST</span>
            <input type="number" step="0.01" value={form.igst_amount} onChange={e => set('igst_amount', e.target.value)} onBlur={computeTotal} style={inputStyle} placeholder="0.00" />
          </label>
          <label style={fieldLabel}>
            <span>TDS Section</span>
            <select value={form.tds_section_code} onChange={e => set('tds_section_code', e.target.value)} style={inputStyle}>
              {TDS_SECTIONS.map(s => <option key={s} value={s}>{s || '— None —'}</option>)}
            </select>
          </label>
          <label style={fieldLabel}>
            <span>TDS Amount</span>
            <input type="number" step="0.01" value={form.tds_amount} onChange={e => set('tds_amount', e.target.value)} onBlur={computeTotal} style={inputStyle} placeholder="0.00" />
          </label>
          <label style={fieldLabel}>
            <span style={{ fontWeight: 600, color: '#111827' }}>Total Amount *</span>
            <input type="number" step="0.01" value={form.total_amount} onChange={e => set('total_amount', e.target.value)}
              style={{ ...inputStyle, fontWeight: 700, fontSize: '1rem', background: '#fefce8', borderColor: '#eab308' }} placeholder="0.00" />
          </label>
          <div style={{ ...fieldLabel, justifyContent: 'flex-end' }}>
            <span style={{ color: '#9ca3af', fontSize: '0.72rem' }}>Tax & Reverse-Charge Flags</span>
            <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', padding: '0.4rem 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.is_itc_eligible} onChange={e => set('is_itc_eligible', e.target.checked)} />
                <span>ITC eligible</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.is_rcm} onChange={e => set('is_rcm', e.target.checked)} />
                <span>RCM</span>
              </label>
            </div>
          </div>
        </div>

        {/* Live summary panel */}
        <div style={{ background: totalMismatch ? '#fef3c7' : '#f9fafb', border: '1px solid ' + (totalMismatch ? '#f59e0b' : '#e5e7eb'), borderRadius: 6, padding: '0.65rem 1rem', fontSize: '0.85rem', display: 'flex', flexWrap: 'wrap', gap: '0.8rem 1.6rem', alignItems: 'center' }}>
          <span><span style={{ color: '#6b7280' }}>Subtotal:</span> <strong>{fmtMoney(sub)}</strong></span>
          <span><span style={{ color: '#6b7280' }}>+ Tax:</span> <strong>{fmtMoney(taxes)}</strong></span>
          <span><span style={{ color: '#6b7280' }}>− TDS:</span> <strong>{fmtMoney(tds)}</strong></span>
          <span style={{ marginLeft: 'auto' }}>
            <span style={{ color: '#6b7280' }}>Computed:</span> <strong style={{ color: totalMismatch ? '#b45309' : '#111827' }}>{fmtMoney(computedTotal)}</strong>
            {totalMismatch && <span style={{ color: '#b45309', marginLeft: '0.6rem', fontSize: '0.78rem' }}>⚠ Total entered ({fmtMoney(totalEntered)}) doesn't match computed</span>}
          </span>
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
          💡 For cash payouts with no tax breakdown, just enter <strong>Total Amount</strong> — Subtotal will auto-fill.
        </div>
      </div>

      {/* ─── Sticky action bar ─── */}
      <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #e5e7eb', padding: '0.8rem 1rem', marginTop: '0.5rem', borderRadius: '0 0 8px 8px', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', boxShadow: '0 -2px 8px rgba(0,0,0,0.04)' }}>
        <button onClick={() => setForm(emptyForm)} disabled={saving} style={{ padding: '0.55rem 1.1rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer', fontSize: '0.9rem' }}>
          Reset
        </button>
        <button onClick={() => saveDraft(false)} disabled={saving} style={{ padding: '0.55rem 1.1rem', border: '1px solid #2563eb', borderRadius: 6, background: '#fff', color: '#2563eb', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
          {saving ? 'Saving…' : 'Save as Draft'}
        </button>
        <button onClick={() => saveDraft(true)} disabled={saving} style={{ padding: '0.55rem 1.3rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
          {saving ? 'Saving…' : 'Save & Submit'}
        </button>
      </div>
    </div>
  );
}

export default PmsExpenseEntry;
